// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). The
// miniatures catalog — factions/armies, units (points + build/paint status), the
// paint-recipe library, and the paint inventory — never leaves the device. Only the
// DERIVED-SIGNAL aggregates (computeSignals → signals.rs) are sent to the brain.

import { invoke } from "./tauri-web";

// ── Domain types (mirror src-tauri/src/db.rs) ─────────────────────────────────────

/** The five model statuses, in pipeline order. */
export const STATUSES = ["unbuilt", "built", "primed", "wip", "painted"] as const;
export type Status = (typeof STATUSES)[number];

/** Human label + accent per status (slate-toned, never traffic-light). */
export const STATUS_LABEL: Record<Status, string> = {
  unbuilt: "Unbuilt",
  built: "Built",
  primed: "Primed",
  wip: "WIP",
  painted: "Painted",
};
export const STATUS_COLOR: Record<Status, string> = {
  unbuilt: "#9aa3af", // grey — bare sprue
  built: "#7c8794",   // slate
  primed: "#8a7f9b",  // muted violet-grey
  wip: "#c08a3e",     // amber — in progress
  painted: "#4f8a6d", // muted green — done
};
export function statusLabel(s: string): string {
  return (STATUS_LABEL as Record<string, string>)[s] ?? s;
}
export function statusColor(s: string): string {
  return (STATUS_COLOR as Record<string, string>)[s] ?? "#9aa3af";
}

export interface Faction {
  id: string;
  name: string;
  game_system: string;
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewFaction { name: string; game_system?: string; notes?: string }
export type FactionPatch = Partial<{ name: string; game_system: string; notes: string }>;

export interface Unit {
  id: string;
  faction_id: string;
  name: string;
  points: number;
  model_count: number;
  status: Status;
  recipe_id: string | null;
  wip_since: string | null;
  notes: string;
  /** On-device photo of the painted model (compressed-JPEG data-URL), or "". Never sent to the brain. */
  photo_ref: string;
  created_at: string;
  updated_at: string;
}
export interface NewUnit {
  faction_id: string;
  name: string;
  points?: number;
  model_count?: number;
  status?: Status;
  recipe_id?: string | null;
  notes?: string;
  photo_ref?: string;
}
export type UnitPatch = Partial<{
  faction_id: string;
  name: string;
  points: number;
  model_count: number;
  status: Status;
  /** Pass `null` to clear the recipe link; omit to leave it. */
  recipe_id: string | null;
  notes: string;
  photo_ref: string;
}>;

export interface Recipe {
  id: string;
  name: string;
  steps: string[];
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewRecipe { name: string; steps?: string[]; notes?: string }
export type RecipePatch = Partial<{ name: string; steps: string[]; notes: string }>;

export interface Paint {
  id: string;
  brand: string;
  name: string;
  paint_type: string;
  color_family: string;
  qty: number;
  /** 0 = owned (on the shelf); 1 = on the shopping list (a paint you still need to buy). */
  wanted: number;
  created_at: string;
  updated_at: string;
}
export interface NewPaint {
  brand?: string;
  name: string;
  paint_type?: string;
  color_family?: string;
  qty?: number;
  wanted?: number;
}
export type PaintPatch = Partial<{
  brand: string;
  name: string;
  paint_type: string;
  color_family: string;
  qty: number;
  wanted: number;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::CollectionSignals. Every field is a computed
// aggregate — counts, points sums, recipe use-frequencies, WIP ages, paint
// distributions — never a raw note or photo ref. This is the payload the AI recap
// reasons over (see brain.ts), so the model names SPECIFIC figures from the user's own
// corpus instead of re-reading the catalog.
export interface NameCount { name: string; count: number }
export interface Totals {
  factions: number;
  units: number;
  models: number;
  recipes: number;
  paints: number;
  paint_bottles: number;
}
export interface PointsByStatus { status: Status; points: number; units: number; models: number }
export interface PointsBreakdown {
  total: number;
  painted: number;
  unpainted: number;
  painted_pct: number | null;
  by_status: PointsByStatus[];
}
export interface StatusCount {
  status: Status;
  units: number;
  models: number;
  pct_of_models: number | null;
}
export interface FactionStat {
  name: string;
  game_system: string;
  units: number;
  models: number;
  total_points: number;
  painted_points: number;
  painted_pct: number | null;
}
export interface RecipeStat {
  name: string;
  step_count: number;
  units_using: number;
  models_using: number;
}
export interface WipItem { name: string; days: number; since: string }
export interface WipStats {
  count: number;
  oldest_days: number | null;
  over_year: number;
  avg_days: number | null;
  items: WipItem[];
}
export interface PaintStats {
  distinct: number;
  bottles: number;
  by_color_family: NameCount[];
  by_brand: NameCount[];
}
export interface CollectionSignals {
  schema: string;
  generated_for: string;
  totals: Totals;
  points: PointsBreakdown;
  status_counts: StatusCount[];
  factions: FactionStat[];
  recipes: RecipeStat[];
  wip: WipStats;
  paints: PaintStats;
}

export const store = {
  // Factions
  listFactions: () => invoke<Faction[]>("list_factions"),
  createFaction: (input: NewFaction) => invoke<Faction>("create_faction", { input }),
  updateFaction: (id: string, patch: FactionPatch) => invoke<void>("update_faction", { id, patch }),
  deleteFaction: (id: string) => invoke<void>("delete_faction", { id }),
  // Units
  listUnits: (factionId?: string) => invoke<Unit[]>("list_units", { factionId: factionId ?? null }),
  getUnit: (id: string) => invoke<Unit | null>("get_unit", { id }),
  createUnit: (input: NewUnit) => invoke<Unit>("create_unit", { input }),
  updateUnit: (id: string, patch: UnitPatch) => invoke<void>("update_unit", { id, patch }),
  deleteUnit: (id: string) => invoke<void>("delete_unit", { id }),
  // Recipes
  listRecipes: () => invoke<Recipe[]>("list_recipes"),
  createRecipe: (input: NewRecipe) => invoke<Recipe>("create_recipe", { input }),
  updateRecipe: (id: string, patch: RecipePatch) => invoke<void>("update_recipe", { id, patch }),
  deleteRecipe: (id: string) => invoke<void>("delete_recipe", { id }),
  // Paints
  listPaints: () => invoke<Paint[]>("list_paints"),
  createPaint: (input: NewPaint) => invoke<Paint>("create_paint", { input }),
  updatePaint: (id: string, patch: PaintPatch) => invoke<void>("update_paint", { id, patch }),
  deletePaint: (id: string) => invoke<void>("delete_paint", { id }),
  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so WIP ages align to the user's clock. */
  computeSignals: (today: string) => invoke<CollectionSignals>("compute_signals", { today }),
};

// ── Pure helpers (UI + local quick-stats, no AI) ────────────────────────────────

/** Curated paint color-family suggestions; user can also type their own. */
export const COLOR_FAMILIES = [
  "red", "orange", "yellow", "green", "blue", "purple",
  "brown", "flesh", "bone", "white", "grey", "black",
  "metallic", "gold", "silver",
];
/** Curated paint-type suggestions. */
export const PAINT_TYPES = ["base", "layer", "shade", "contrast", "dry", "technical", "metallic", "primer"];

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Days a WIP unit has been sitting, from its wip_since date to today (local). */
export function wipAgeDays(wipSince: string | null, today = new Date()): number | null {
  if (!wipSince) return null;
  const since = toLocalDate(wipSince);
  if (!since) return null;
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const ms = a.getTime() - since.getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

export interface QuickStats {
  units: number;
  models: number;
  totalPoints: number;
  paintedPoints: number;
  unpaintedPoints: number;
  paintedPct: number | null;
  wipCount: number;
}

/** Local at-a-glance roll-up across ALL units (no AI, instant). */
export function quickStats(units: Unit[]): QuickStats {
  let models = 0, totalPoints = 0, paintedPoints = 0, wipCount = 0;
  for (const u of units) {
    models += u.model_count;
    totalPoints += u.points;
    if (u.status === "painted") paintedPoints += u.points;
    if (u.status === "wip") wipCount += 1;
  }
  const unpaintedPoints = totalPoints - paintedPoints;
  return {
    units: units.length,
    models,
    totalPoints,
    paintedPoints,
    unpaintedPoints,
    paintedPct: totalPoints > 0 ? (paintedPoints / totalPoints) * 100 : null,
    wipCount,
  };
}

/** Per-status model counts across all units (for the progress bar). */
export function statusModelCounts(units: Unit[]): Record<Status, number> {
  const out: Record<Status, number> = { unbuilt: 0, built: 0, primed: 0, wip: 0, painted: 0 };
  for (const u of units) out[u.status] += u.model_count;
  return out;
}

/** How many steps a status sits from "painted" (0 = painted itself). Used to rank
 *  not-yet-painted units by how close a session of work would get them to done. */
const STEPS_FROM_DONE: Record<Status, number> = { painted: 0, wip: 1, primed: 2, built: 3, unbuilt: 4 };

/**
 * "Pick up next" — the decision a player actually faces each session, not a tally.
 * Ranks every not-yet-painted unit so the answer to "what should I paint next" is the
 * TOP row, never insertion order:
 *   1. units already WIP, oldest `wip_since` first (the real pile-of-shame risk — the
 *      longer something has sat mid-paint, the more likely it never gets finished);
 *   2. everything else, closest to "painted" first (primed before built before
 *      unbuilt) — the quickest real progress available right now.
 * No competitor researched (Sprue, Liber Pigmenta, The Painting Ledger, Pile of
 * Potential, PileOfShame) ranks by either signal — every one of them is a flat/filterable
 * catalog or kanban with a completion percentage, never a single ranked "next" decision.
 */
export function nextUpRanked(units: Unit[], today = new Date()): Unit[] {
  const pending = units.filter((u) => u.status !== "painted");
  const wip = pending
    .filter((u) => u.status === "wip")
    .sort((a, b) => (wipAgeDays(b.wip_since, today) ?? 0) - (wipAgeDays(a.wip_since, today) ?? 0));
  const rest = pending
    .filter((u) => u.status !== "wip")
    .sort((a, b) => STEPS_FROM_DONE[a.status] - STEPS_FROM_DONE[b.status]);
  return [...wip, ...rest];
}
